import React from 'react';

import classNames from 'classnames';
import styled from 'styled-components';

import { CommunityActivityRecipientType, CommunityActivityType } from 'types/communities';

import ActivityContent from './ActivityContent';
import ActivityHeader from './ActivityHeader';
import ActivityTypeIcon from './ActivityTypeIcon';
import { ACTIVITY_DIRECTION_TYPE, ACTIVITY_EMAIL_RECIPIENT_ROLE } from './constants';

type ActivityBubbleMessageProps = {
  activity: CommunityActivityType;
  communityId?: number;
  showRenderedBody: boolean;
  onToggleRenderedBody: () => void;
  onActivityUpdated?: () => void | Promise<unknown>;
};

const Container = styled.div`
  position: relative;
  display: flex;
  width: min(100%, 700px);
  max-width: 100%;
  min-width: 150px;
  min-width: min(150px, 100%);
  box-sizing: border-box;
  min-inline-size: 0;
`;

const Bubble = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  box-sizing: border-box;
  overflow: hidden;
  padding: 9px 12px 8px 30px;
  border: 1px solid var(--line-gray);
  border-radius: 10px;
  background: var(--background-gray-light);

  &.direction-outbound {
    background: var(--light-blue);
  }

  &.direction-inbound {
    background: var(--background);
  }

  &:hover,
  &:focus-within {
    .upload-transcript-action {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

const getFromRecipientText = (recipients: CommunityActivityRecipientType[]) => {
  const fromRecipient = recipients.find((recipient) => recipient.role === ACTIVITY_EMAIL_RECIPIENT_ROLE.FROM);

  if (!fromRecipient) {
    return null;
  }

  return fromRecipient.label || fromRecipient.email;
};

const ActivityBubbleMessage = ({
  activity,
  communityId,
  showRenderedBody,
  onToggleRenderedBody,
  onActivityUpdated,
}: ActivityBubbleMessageProps) => {
  const fromRecipientText = getFromRecipientText(activity.email?.recipients ?? []);
  const hasRenderedHtml = Boolean(activity.email?.display_html_body || activity.email?.html_body);

  return (
    <Container>
      <ActivityTypeIcon activityTypeSlug={activity.activity_type_slug} />
      <Bubble
        className={classNames({
          'direction-inbound': activity.direction_type === ACTIVITY_DIRECTION_TYPE.INBOUND,
          'direction-outbound': activity.direction_type === ACTIVITY_DIRECTION_TYPE.OUTBOUND,
          'direction-unknown': activity.direction_type === ACTIVITY_DIRECTION_TYPE.UNKNOWN,
        })}
      >
        <ActivityHeader
          title={activity.title}
          directionType={activity.direction_type}
          outcomeValue={activity.outcome}
          outcomeLabel={activity.outcome_label ?? activity.metadata?.outcome_label}
          actorName={activity.actor?.name}
          occurredAt={activity.occurred_at}
          fromRecipientText={fromRecipientText}
          hasRenderedHtml={hasRenderedHtml}
          ringCentralRecordId={activity.ringcentral_communication?.id}
          showRenderedBody={showRenderedBody}
          onToggleRenderedBody={onToggleRenderedBody}
        />
        <ActivityContent
          activity={activity}
          communityId={communityId}
          showRenderedBody={showRenderedBody}
          hideSummary
          onActivityUpdated={onActivityUpdated}
        />
      </Bubble>
    </Container>
  );
};

export default ActivityBubbleMessage;
